import { AnyObject } from '@store/types';
import { action, override, makeObservable } from 'mobx';
import groupBy from 'lodash/groupBy';
import forIn from 'lodash/forIn';

import type { Optional } from 'chaika-common-types';

import { List } from './List';

export type GroupedListParams<T, I = string> = T & { ids: I[]; count: number; groupId: string };

export abstract class GroupedList<ListParams, CreateParams, FilterParams = null> extends List<
    GroupedListParams<ListParams>,
    CreateParams,
    FilterParams
> {
    constructor(params?: CreateParams[]) {
        super(params);
        makeObservable(this);
    }

    public get itemIds(): string[] {
        return this.values.flatMap(({ ids }) => ids);
    }

    @override
    public get filteredValuesIds(): string[] {
        return this.filteredValues.map(value => this.toStringGroupParams(value as any));
    }

    @override
    public set(params: CreateParams[]): void {
        this.reset();
        const grouped = groupBy(params, this.toStringGroupParams);

        forIn(grouped, values => {
            const ids = values.map(value => this.getId(value));
            const value = this.mergeCreateParams(values as CreateParams[]);
            const count = ids.length;

            const groupId = this.toStringGroupParams(value);

            this.list.set(
                this.toStringGroupParams(value),
                this.create({
                    ...value,
                    ids,
                    count,
                    groupId,
                } as CreateParams),
            );
        });
    }

    @override
    public addIfNotExist(params: CreateParams[]): void {
        params.forEach(createParams => {
            const groupId = this.toStringGroupParams(createParams);

            const item = this.list.get(groupId);

            if (!!item && !item.ids.includes(this.getId(createParams))) {
                this.addItems(groupId, [this.getId(createParams)]);
            } else if (!item) {
                this.list.set(
                    groupId,
                    this.create({
                        groupId,
                        ids: [this.getId(createParams)],
                        count: 1,
                        ...createParams,
                    }),
                );
            }
        });
    }

    @override
    public add(params: CreateParams[]): void {
        params.forEach(createParams => {
            const groupId = this.toStringGroupParams(createParams);

            if (this.has(groupId)) {
                this.addItems(groupId, [this.getId(createParams)]);
            } else {
                this.list.set(
                    groupId,
                    this.create({
                        groupId,
                        ids: [this.getId(createParams)],
                        count: 1,
                        ...createParams,
                    }),
                );
            }
        });
    }

    @override
    public update(id: string, value: CreateParams): void {
        const group = this.get(id);

        if (!group) {
            return;
        }

        const { ids, count, ...params } = group;
        const groupId = this.toStringGroupParams({ ...params, ...value });

        if (id !== groupId) {
            const newData = this.create(value);
            this.list.delete(id);
            this.list.set(groupId, { ...params, ...newData, ids, count, groupId });
        } else {
            super.update(id, { ids, count, ...(value as CreateParams) });
        }
    }

    @action
    public removeItems(groupId: string, removingIds: string[]) {
        const group = this.get(groupId);

        if (!group) {
            return;
        }

        const { ids, count, groupId: _, ...params } = group;

        const newIds = ids.filter(id => !removingIds.includes(id));

        this.list.delete(groupId);

        if (newIds.length > 0) {
            this.list.set(groupId, {
                groupId,
                ids: newIds,
                count: newIds.length,
                ...params,
            } as GroupedListParams<ListParams>);
        }
    }

    @action
    public addItems(groupId: string, addIds: string[]): void {
        const { ids, count, groupId: _, ...params } = this.getExisting(groupId);

        const newIds = [...ids, ...addIds];

        this.list.set(groupId, {
            groupId,
            ids: newIds,
            count: newIds.length,
            ...params,
        } as GroupedListParams<ListParams>);
    }

    public getFilteredValuesIds(filterParams: FilterParams): string[] {
        return this.getFilteredValues(filterParams).map(({ groupId }) => groupId);
    }

    public getByItemId(id: string): Optional<ListParams> {
        return this.values.find(it => (it as AnyObject)[this.identifiableFieldName] === id);
    }

    protected abstract toStringGroupParams(params: CreateParams | GroupedListParams<ListParams>): string;

    protected mergeCreateParams(params: CreateParams[]): CreateParams {
        return params[0];
    }
}
